// contexts/UnsavedChangesContext.tsx
import { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { useRouter } from 'next/router';
import { Modal, Button } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';

interface UnsavedChangesContextType {
    setCheckFn: (fn: (() => boolean) | null) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType | null>(null);

export const UnsavedChangesProvider = ({ children }: { children: ReactNode }) => {
    const router = useRouter();
    const [checkFn, setCheckFnState] = useState<(() => boolean) | null>(null);
    const [modal, setModal] = useState<{ show: boolean; pendingUrl: string | null }>({
        show: false,
        pendingUrl: null,
    });
    const [isConfirmingLeave, setIsConfirmingLeave] = useState(false);

    // Wrap useState setter: storing a function in state must use updater form,
    // otherwise React treats the fn itself as SetStateAction updater.
    const setCheckFn = useCallback((fn: (() => boolean) | null) => {
        setCheckFnState(() => fn);
    }, []);

    const hasUnsavedChanges = useCallback(() => {
        // Tạm thời disable check khi đang confirm leave
        if (isConfirmingLeave) {
            return false;
        }
        if (typeof checkFn === 'function') {
            return checkFn();
        }
        return false;
    }, [checkFn, isConfirmingLeave]);

    // Browser close/refresh
    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges()) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsavedChanges]);

    // Router navigation
    useEffect(() => {
        const handleRouteChange = (url: string) => {
            if (url === router.asPath) return;
            if (hasUnsavedChanges()) {
                setModal({ show: true, pendingUrl: url });
                router.events.emit(
                    'routeChangeError',
                    Object.assign(new Error('Route change blocked by unsaved changes'), { cancelled: true }),
                    url
                );
                throw 'Route change blocked by unsaved changes';
            }
        };

        router.events.on('routeChangeStart', handleRouteChange);
        return () => router.events.off('routeChangeStart', handleRouteChange);
    }, [hasUnsavedChanges, router]);

    // Back/Forward button
    useEffect(() => {
        router.beforePopState(({ url }) => {
            if (hasUnsavedChanges()) {
                setModal({ show: true, pendingUrl: url });
                window.history.pushState(null, '', router.asPath);
                return false;
            }
            return true;
        });
        return () => router.beforePopState(() => true);
    }, [hasUnsavedChanges, router]);

    const confirmLeave = async () => {
        const url = modal.pendingUrl;
        setModal({ show: false, pendingUrl: null });
        // Tạm thời disable check để cho phép navigate
        setIsConfirmingLeave(true);
        
        // Reset flag sau khi route change hoàn thành
        const handleRouteChangeComplete = () => {
            setIsConfirmingLeave(false);
            router.events.off('routeChangeComplete', handleRouteChangeComplete);
        };
        router.events.on('routeChangeComplete', handleRouteChangeComplete);
        
        // Fallback: reset sau 1 giây nếu event không fire
        setTimeout(() => {
            setIsConfirmingLeave(false);
            router.events.off('routeChangeComplete', handleRouteChangeComplete);
        }, 1000);
        
        if (url) {
            await router.push(url);
        }
    };

    const cancelLeave = () => setModal({ show: false, pendingUrl: null });

    // Giữ identity ổn định — tránh consumer useEffect([context]) chạy lại mỗi render Provider
    const contextValue = useMemo(() => ({ setCheckFn }), [setCheckFn]);

    return (
        <UnsavedChangesContext.Provider value={contextValue}>
            {children}

            {/* Global Modal - render 1 lần duy nhất */}
            <Modal
                open={modal.show}
                onCancel={cancelLeave}
                footer={[
                    <Button key="cancel" onClick={cancelLeave}>
                        Ở lại
                    </Button>,
                    <Button key="confirm" type="primary" danger onClick={confirmLeave}>
                        Rời đi
                    </Button>,
                ]}
                centered
                closable={false}
                maskClosable={false}
                width={480}
            >
                <div style={{ padding: '8px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                        <ExclamationCircleOutlined
                            style={{
                                fontSize: 24,
                                color: '#faad14',
                                marginTop: 2,
                            }}
                        />
                        <div style={{ flex: 1 }}>
                            <h3 style={{ margin: 0, marginBottom: 8, fontSize: 16, fontWeight: 600 }}>
                                Thay đổi chưa lưu!
                            </h3>
                            <p style={{ margin: 0, color: '#666', lineHeight: 1.6 }}>
                                Bạn có chắc muốn rời khỏi trang này? Các thay đổi chưa lưu sẽ bị mất.
                            </p>
                        </div>
                    </div>
                </div>
            </Modal>
        </UnsavedChangesContext.Provider>
    );
};

// Hook đơn giản cho các page
export const useUnsavedChanges = (checkFn: () => boolean) => {
    const context = useContext(UnsavedChangesContext);
    const setCheckFn = context?.setCheckFn;

    useEffect(() => {
        if (typeof checkFn !== 'function' || !setCheckFn) return;
        setCheckFn(checkFn);
        return () => setCheckFn(null);
        // Chỉ đăng ký lại khi identity checkFn đổi; không phụ thuộc object context
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkFn, setCheckFn]);
};